import React, { useState, useEffect } from 'react';
import {
  roleService,
  Role,
  Permission,
  CreateRoleData,
  UpdateRoleData,
} from '../services/roleService';
import { WithPermission } from '../contexts/PermissionsContext';
import { useAuth } from '../contexts/AuthContext';

interface RoleManagementProps {
  onRoleUpdate?: () => void;
}

export const RoleManagement: React.FC<RoleManagementProps> = ({ onRoleUpdate }) => {
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);

  // Check if user is Super Admin
  const isSuperAdmin = user && (user.role_id === 1 || user.role_name === 'Super Admin');

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      if (isSuperAdmin) {
        loadData();
      }
    }
  }, [isAuthenticated, authLoading, isSuperAdmin]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [rolesData, permissionsData] = await Promise.all([
        roleService.getRoles(),
        roleService.getPermissions(),
      ]);
      setRoles(rolesData);
      setPermissions(permissionsData);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load role data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRole = async (roleData: CreateRoleData) => {
    try {
      await roleService.createRole(roleData);
      await loadData();
      onRoleUpdate?.();
      setShowCreateModal(false);
    } catch (err) {
      console.error('Failed to create role:', err);
      setError('Failed to create role');
    }
  };

  const handleUpdateRole = async (roleId: number, updates: UpdateRoleData) => {
    try {
      await roleService.updateRole(roleId, updates);
      await loadData();
      onRoleUpdate?.();
      setEditingRole(null);
    } catch (err) {
      console.error('Failed to update role:', err);
      setError('Failed to update role');
    }
  };

  const handleDeleteRole = async (roleId: number) => {
    if (window.confirm('Are you sure you want to delete this role?')) {
      try {
        await roleService.deleteRole(roleId);
        await loadData();
        onRoleUpdate?.();
      } catch (err) {
        console.error('Failed to delete role:', err);
        setError('Failed to delete role');
      }
    }
  };

  const getPermissionsByCategory = (category: string) => {
    return permissions.filter((p) => p.category === category);
  };

  const getPermissionCategories = () => {
    return ['admin', 'reports', 'page_access'];
  };

  // Show access denied if user is not Super Admin
  if (!isSuperAdmin) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Access Denied</h3>
            <div className="mt-2 text-sm text-red-700">
              Only Super Administrators can access Role Management.
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">Error</h3>
            <div className="mt-2 text-sm text-red-700">{error}</div>
            <div className="mt-4">
              <button
                onClick={loadData}
                className="bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded text-sm"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Role Management</h2>
        <WithPermission permission="admin.user_management">
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
          >
            Create Role
          </button>
        </WithPermission>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {roles.map((role) => (
          <div key={role.id} className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">{role.name}</h3>
              <div className="flex space-x-2">
                <WithPermission permission="admin.user_management">
                  <button
                    onClick={() => setEditingRole(role)}
                    className="text-blue-600 hover:text-blue-900 text-sm"
                  >
                    Edit
                  </button>
                </WithPermission>
                <WithPermission permission="admin.user_management">
                  <button
                    onClick={() => handleDeleteRole(role.id)}
                    className="text-red-600 hover:text-red-900 text-sm"
                  >
                    Delete
                  </button>
                </WithPermission>
              </div>
            </div>

            {role.description && <p className="mt-2 text-sm text-gray-600">{role.description}</p>}

            <div className="mt-4">
              <div className="text-sm font-medium text-gray-700 mb-2">
                Permissions ({role.permissions.length}):
              </div>
              <div className="space-y-2">
                {getPermissionCategories().map((category) => {
                  const categoryPermissions = getPermissionsByCategory(category);
                  const rolePermissionsInCategory = role.permissions.filter((p) =>
                    categoryPermissions.some((cp) => cp.id === p)
                  );

                  if (rolePermissionsInCategory.length === 0) return null;

                  return (
                    <div key={category}>
                      <div className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        {category.replace('_', ' ')}
                      </div>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {rolePermissionsInCategory.map((permissionId) => {
                          const permission = permissions.find((p) => p.id === permissionId);
                          return (
                            <span
                              key={permissionId}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              {permission?.name || permissionId}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Create/Edit Role Modal */}
      {(showCreateModal || editingRole) && (
        <RoleModal
          role={editingRole}
          permissions={permissions}
          onClose={() => {
            setShowCreateModal(false);
            setEditingRole(null);
          }}
          onSave={async (data) => {
            if (editingRole) {
              await handleUpdateRole(editingRole.id, data as UpdateRoleData);
            } else {
              await handleCreateRole(data as CreateRoleData);
            }
          }}
        />
      )}
    </div>
  );
};

interface RoleModalProps {
  role?: Role | null;
  permissions: Permission[];
  onClose: () => void;
  onSave: (data: CreateRoleData | UpdateRoleData) => Promise<void>;
}

const RoleModal: React.FC<RoleModalProps> = ({ role, permissions, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: role?.name || '',
    description: role?.description || '',
    permissions: role?.permissions || [],
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(formData);
  };

  const handlePermissionToggle = (permissionId: string) => {
    setFormData((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(permissionId)
        ? prev.permissions.filter((p) => p !== permissionId)
        : [...prev.permissions, permissionId],
    }));
  };

  const getPermissionsByCategory = (category: string) => {
    return permissions.filter((p) => p.category === category);
  };

  const getPermissionCategories = () => {
    return ['admin', 'reports', 'page_access'];
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {role ? 'Edit Role' : 'Create Role'}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Role Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={3}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Permissions</label>
              <div className="space-y-4 max-h-64 overflow-y-auto">
                {getPermissionCategories().map((category) => {
                  const categoryPermissions = getPermissionsByCategory(category);
                  if (categoryPermissions.length === 0) return null;

                  return (
                    <div key={category}>
                      <div className="text-sm font-medium text-gray-700 mb-2">
                        {category.replace('_', ' ').toUpperCase()}
                      </div>
                      <div className="space-y-2 ml-4">
                        {categoryPermissions.map((permission) => (
                          <label key={permission.id} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={formData.permissions.includes(permission.id)}
                              onChange={() => handlePermissionToggle(permission.id)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <div className="ml-2">
                              <div className="text-sm font-medium text-gray-900">
                                {permission.name}
                              </div>
                              <div className="text-xs text-gray-500">{permission.description}</div>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
              >
                {role ? 'Update Role' : 'Create Role'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
